export const SIGNAL_EVENTS_COLUMN_SLOTS = [
  "Time",
  "Signal",
  "Event",
  "Event Y",
  "Event Label",
] as const;

export type SignalEventsPluginConfig = {
  timeColumn?: string;
  signalColumn?: string;
  eventColumn?: string;
  eventYColumn?: string;
  eventLabelColumn?: string;
};

export type SignalEventsColumnMapping = {
  timeColumn: string;
  signalColumn: string;
  eventColumn: string;
  eventYColumn?: string;
  eventLabelColumn?: string;
};

export type ResolveSignalEventsMappingArgs = {
  availableColumns: string[];
  configuredColumns?: Array<string | null | undefined>;
  pluginConfig?: SignalEventsPluginConfig;
};

export type ResolveSignalEventsMappingResult =
  | {
    ok: true;
    mapping: SignalEventsColumnMapping;
    warnings: string[];
  }
  | {
    ok: false;
    error: string;
    warnings: string[];
  };

export type SignalSample = {
  x: number;
  y: number;
};

export type EventSample = {
  x: number;
  y: number;
  label?: string;
};

export type NormalizeSignalEventsResult = {
  signal: SignalSample[];
  events: EventSample[];
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  warnings: string[];
};

function normalizeColumnKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function resolveArrayColumn(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (ArrayBuffer.isView(value)) {
    return Array.from(value as unknown as Iterable<unknown>);
  }

  if (
    value
    && typeof value === "object"
    && "length" in value
    && typeof (value as { length?: unknown }).length === "number"
  ) {
    return Array.from(value as ArrayLike<unknown>);
  }

  return [];
}

export function normalizePerspectiveColumns(
  columnsPayload: object
): Record<string, unknown[]> {
  const normalized: Record<string, unknown[]> = {};

  for (const [columnName, columnData] of Object.entries(columnsPayload)) {
    if (columnName === "__ROW_PATH__") {
      continue;
    }

    normalized[columnName] = resolveArrayColumn(columnData);
  }

  return normalized;
}

function pickColumn(
  availableColumns: string[],
  excluded: Set<string>,
  explicitCandidates: Array<string | null | undefined>,
  exactAliases: string[],
  includeAliases: string[]
): string | undefined {
  const byNormalized = new Map<string, string>();
  for (const column of availableColumns) {
    byNormalized.set(normalizeColumnKey(column), column);
  }

  const maybeUse = (candidate: string | null | undefined): string | undefined => {
    if (!candidate) return undefined;
    const matched = byNormalized.get(normalizeColumnKey(candidate));
    if (!matched || excluded.has(matched)) return undefined;
    return matched;
  };

  for (const candidate of explicitCandidates) {
    const explicit = maybeUse(candidate);
    if (explicit) return explicit;
  }

  for (const alias of exactAliases) {
    const match = maybeUse(alias);
    if (match) return match;
  }

  for (const column of availableColumns) {
    if (excluded.has(column)) continue;
    const normalized = normalizeColumnKey(column);
    if (includeAliases.some((alias) => normalized.includes(alias))) {
      return column;
    }
  }

  return undefined;
}

function requiredColumnError(missing: string, availableColumns: string[]): string {
  const available = availableColumns.length > 0
    ? availableColumns.join(", ")
    : "none";
  return `${missing} column not found. Configure ${SIGNAL_EVENTS_COLUMN_SLOTS.join("/")} in the viewer settings. Available columns: ${available}.`;
}

export function resolveSignalEventsMapping({
  availableColumns,
  configuredColumns,
  pluginConfig,
}: ResolveSignalEventsMappingArgs): ResolveSignalEventsMappingResult {
  const deduped = Array.from(new Set(availableColumns));
  const selected = configuredColumns ?? [];
  const used = new Set<string>();
  const warnings: string[] = [];

  const timeColumn = pickColumn(
    deduped,
    used,
    [pluginConfig?.timeColumn, selected[0]],
    ["time", "timestamp", "ts", "epoch"],
    ["time", "timestamp", "epoch"]
  );

  if (!timeColumn) {
    return {
      ok: false,
      error: requiredColumnError("Time", deduped),
      warnings,
    };
  }

  used.add(timeColumn);

  const signalColumn = pickColumn(
    deduped,
    used,
    [pluginConfig?.signalColumn, selected[1]],
    ["signal", "value", "waveform", "sample", "ecg", "ppg"],
    ["signal", "wave", "value", "ecg", "ppg", "amplitude", "voltage"]
  );

  if (!signalColumn) {
    return {
      ok: false,
      error: requiredColumnError("Signal", deduped),
      warnings,
    };
  }

  used.add(signalColumn);

  const eventColumn = pickColumn(
    deduped,
    used,
    [pluginConfig?.eventColumn, selected[2]],
    ["event", "events", "eventflag", "flag", "alert", "peak"],
    ["event", "alert", "flag", "peak", "detect", "trigger"]
  );

  if (!eventColumn) {
    return {
      ok: false,
      error: requiredColumnError("Event", deduped),
      warnings,
    };
  }

  used.add(eventColumn);

  const eventYColumn = pickColumn(
    deduped,
    used,
    [pluginConfig?.eventYColumn, selected[3]],
    ["event_y", "eventy"],
    ["eventy", "eventvalue", "markery"]
  );

  if (!eventYColumn) {
    warnings.push("Event Y column not configured; event markers will use Signal values.");
  }

  if (eventYColumn) {
    used.add(eventYColumn);
  }

  const eventLabelColumn = pickColumn(
    deduped,
    used,
    [pluginConfig?.eventLabelColumn, selected[4]],
    ["event_label", "eventlabel", "label"],
    ["eventlabel", "label", "eventname", "annotation"]
  );

  return {
    ok: true,
    mapping: {
      timeColumn,
      signalColumn,
      eventColumn,
      eventYColumn,
      eventLabelColumn,
    },
    warnings,
  };
}

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : undefined;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return undefined;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  if (typeof value === "bigint") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

function toEventFlag(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) && value !== 0;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized.length === 0) return false;
    if (["true", "t", "yes", "y"].includes(normalized)) return true;
    if (["false", "f", "no", "n"].includes(normalized)) return false;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed !== 0;
  }

  return false;
}

function toOptionalLabel(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toString();
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  return undefined;
}

export function normalizeSignalEventsData(
  columns: Record<string, unknown[]>,
  mapping: SignalEventsColumnMapping
): NormalizeSignalEventsResult {
  const timeValues = columns[mapping.timeColumn] ?? [];
  const signalValues = columns[mapping.signalColumn] ?? [];
  const eventValues = columns[mapping.eventColumn] ?? [];
  const eventYValues = mapping.eventYColumn ? (columns[mapping.eventYColumn] ?? []) : [];
  const eventLabelValues = mapping.eventLabelColumn ? (columns[mapping.eventLabelColumn] ?? []) : [];

  const rowCount = Math.max(
    timeValues.length,
    signalValues.length,
    eventValues.length,
    eventYValues.length,
    eventLabelValues.length
  );

  const signal: SignalSample[] = [];
  const events: EventSample[] = [];
  let skippedRows = 0;

  for (let index = 0; index < rowCount; index += 1) {
    const x = toFiniteNumber(timeValues[index]);
    const y = toFiniteNumber(signalValues[index]);

    if (x === undefined || y === undefined) {
      skippedRows += 1;
      continue;
    }

    signal.push({ x, y });

    if (!toEventFlag(eventValues[index])) {
      continue;
    }

    const eventY = toFiniteNumber(eventYValues[index]) ?? y;
    const eventLabel = toOptionalLabel(eventLabelValues[index]);
    events.push({
      x,
      y: eventY,
      label: eventLabel,
    });
  }

  const allY = signal.map((point) => point.y).concat(events.map((point) => point.y));
  const xValues = signal.map((point) => point.x);
  const warnings: string[] = [];

  if (skippedRows > 0) {
    warnings.push(`${skippedRows} row(s) skipped due to invalid time/signal values.`);
  }

  if (signal.length === 0 || allY.length === 0 || xValues.length === 0) {
    return {
      signal,
      events,
      xMin: 0,
      xMax: 1,
      yMin: 0,
      yMax: 1,
      warnings,
    };
  }

  const xMinRaw = Math.min(...xValues);
  const xMaxRaw = Math.max(...xValues);
  const yMinRaw = Math.min(...allY);
  const yMaxRaw = Math.max(...allY);

  const xPad = xMinRaw === xMaxRaw ? 1 : 0;
  const yPad = yMinRaw === yMaxRaw ? Math.max(1, Math.abs(yMinRaw) * 0.05) : 0;

  return {
    signal,
    events,
    xMin: xMinRaw - xPad,
    xMax: xMaxRaw + xPad,
    yMin: yMinRaw - yPad,
    yMax: yMaxRaw + yPad,
    warnings,
  };
}
